/**
 * Sprite box size and image fitting (spec 005).
 *
 * Every renderer (SceneCanvas, SceneRuntime) draws a sprite's box
 * at `sprite.size`, 100×100 when absent, and the state image inside it with
 * "contain" fitting: as large as fits, centred, never stretched.
 */

import type { Sprite, SpriteSize } from '../@types';

/** The box of a sprite saved before `Sprite.size` existed. */
export const DEFAULT_SPRITE_SIZE: Readonly<SpriteSize> = Object.freeze({ width: 100, height: 100 });

/** The sprite's box in canvas pixels (100×100 when it has no valid size). */
export function resolveSpriteSize(sprite: Pick<Sprite, 'size'>): SpriteSize {
  const s = sprite.size;
  if (s && s.width > 0 && s.height > 0 && Number.isFinite(s.width) && Number.isFinite(s.height)) {
    return { width: s.width, height: s.height };
  }
  return { ...DEFAULT_SPRITE_SIZE };
}

/** Where an image of `natural` size lands inside `box`, contain-fitted and centred (box-relative). */
export function containRect(
  box: SpriteSize,
  natural: SpriteSize,
): { x: number; y: number; width: number; height: number } {
  if (!(natural.width > 0) || !(natural.height > 0)) {
    return { x: 0, y: 0, width: box.width, height: box.height };
  }
  const k = Math.min(box.width / natural.width, box.height / natural.height);
  const width = natural.width * k;
  const height = natural.height * k;
  return { x: (box.width - width) / 2, y: (box.height - height) / 2, width, height };
}

/** The longer side of a size derived from a newly assigned image. */
export const SPRITE_IMAGE_LONG_SIDE = 160;

/**
 * A sprite size from an image's natural size: the same proportions with the
 * longer side at 160 px, or the natural size when both sides are smaller
 * (spec 005; spec 026's `fillPictureSlot` uses it too).
 */
export function sizeFromImage(natural: SpriteSize): SpriteSize {
  const { width, height } = natural;
  if (!(width > 0) || !(height > 0)) return { width: 100, height: 100 };
  const long = Math.max(width, height);
  if (long <= SPRITE_IMAGE_LONG_SIDE) return { width, height };
  const k = SPRITE_IMAGE_LONG_SIDE / long;
  return { width: Math.round(width * k), height: Math.round(height * k) };
}
